import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useGetTeamMembers } from '@/api/settings/useGetTeamMembers';
import { Button } from '@/component-library/primitives/Button/Button';
import SettingsLoader from '@/component-library/primitives/Loaders/SettingsLoader';
import NoDataBoard from '@/component-library/widgets/NoDataBoard/NoDataBoard';
import SearchField from '@/component-library/widgets/SearchField/SearchField';
import Table from '@/component-library/widgets/Table/Table';
import { AppRoutes } from '@/utils/routes/router';

import { useTeamMembersTable } from './useTeamMembersTable';

const TeamMembers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useGetTeamMembers();
  const { columns, deleteConfirmationModal } = useTeamMembersTable();
  const [filterValue, setFilterValue] = useState('');

  const filteredData = useMemo(() => {
    if (!filterValue.trim()) {
      return data;
    }

    const searchTerm = filterValue.toLowerCase().trim();

    return data?.filter((row) => {
      // Search by email
      const email = (row.email || '').toLowerCase();
      if (email.includes(searchTerm)) {
        return true;
      }

      // Search by full name (givenName + familyName)
      const givenName = (row.givenName || '').toLowerCase();
      const familyName = (row.familyName || '').toLowerCase();
      const fullName = [givenName, familyName].filter(Boolean).join(' ').trim();
      if (fullName.includes(searchTerm)) {
        return true;
      }

      // Also search individual name parts
      if (givenName.includes(searchTerm) || familyName.includes(searchTerm)) {
        return true;
      }

      return false;
    });
  }, [data, filterValue]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-row items-center mb-4">
        <div className="flex-grow justify-start">
          <SearchField
            initialValue={filterValue}
            setSearchValue={setFilterValue}
            filterTooltipContent={
              <div className="text-sm text-gray-800">
                <div className="font-semibold mb-1">Searchable columns:</div>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>Full Name</li>
                  <li>Email</li>
                </ul>
              </div>
            }
          />
        </div>
        <div className="justify-end items-center">
          <Button onClick={() => navigate(AppRoutes.createTeamMember)} className="!w-[10.5rem]">
            {t('TeamMembersPage.buttonNewTeamMember')}
          </Button>
        </div>
      </div>
      {deleteConfirmationModal}
      {isLoading ? (
        <SettingsLoader />
      ) : filteredData && filteredData.length > 0 ? (
        <Table columns={columns} data={filteredData ?? []} />
      ) : (
        <NoDataBoard description={t('NoDataBoard.addYourFirstTeamMemberLabel')} />
      )}
    </div>
  );
};

export default TeamMembers;
